import { FCX } from "@datassential/platform-ui-lib/lib/core/models";
import cn from "classnames";
import { CommonDataResponseExtended } from "@api/useCommonData";
import { FiChevronDown } from "react-icons/fi";
import SlashSelect from "@components/SlashSelect";
import { useCallback, useMemo } from "react";
import { useCategory } from "@core/CommonDataContext";
import { useSelectFilters } from "./SelectFiltersContext";

const SelectCategoryBlock: FCX<{
	category: NonNullable<CommonDataResponseExtended['categories']>[number];
}> = ({
	className,
	testId,
	category,
}) => {
	const {
		expandedCategories,
		setExpandedCategories,
		filteredCategories,
		selectedFilters,
		setSelectedFilters
	} = useSelectFilters();

	const categoryData = useCategory(category.id);

	const selectedCategoriesCount = useMemo(() => {
		const allCategoriesIds = categoryData?.subCategories?.map(i => i.id ?? 0) ?? [];
		const selectedCategoriesIds = selectedFilters.categories.filter(i => allCategoriesIds.includes(i));
		return selectedCategoriesIds.length;
	}, [selectedFilters.categories, categoryData]);

	const isExpanded = useMemo(() => {
		return expandedCategories.some(i => i === category.id);
	}, [expandedCategories, category.id]);

	const handleExpandIconClick = useCallback(() => {
		setExpandedCategories((prev) => {
			if (!category.id) {
				return prev;
			}
			const newExpandedCategories = [...prev];
			if (newExpandedCategories.includes(category.id)) {
				return newExpandedCategories.filter(i => i !== category.id);
			}
			return [...newExpandedCategories, category.id];
		});
	}, [category.id, setExpandedCategories]);

	const allCategoriesIds = useMemo(() => categoryData?.subCategories?.map(i => i.id ?? 0) ?? [], [categoryData?.subCategories])
	const otherCategoriesMatcher = useCallback((categoryId: number) => !allCategoriesIds.includes(categoryId), [allCategoriesIds])
	const prevCategoriesMatcher = useCallback((categoryId: number) => allCategoriesIds.includes(categoryId), [allCategoriesIds])

	const handleSelect = useCallback((isSelecting: boolean) => () => {
		setSelectedFilters((prevFilters) => {
			const otherCategoriesIds = prevFilters.categories.filter(otherCategoriesMatcher);
			const prevCategoriesIds = prevFilters.categories.filter(prevCategoriesMatcher);
			if (prevCategoriesIds.length === 0) {
				return {
					...prevFilters,
					categories: [...otherCategoriesIds, ...allCategoriesIds],
				};
			}

			if (prevCategoriesIds.length === allCategoriesIds.length) {
				return {
					...prevFilters,
					categories: otherCategoriesIds,
				};
			}

			return {
				...prevFilters,
				categories: isSelecting ? [...otherCategoriesIds, ...allCategoriesIds] : otherCategoriesIds,
			};
		});
	}, [allCategoriesIds, otherCategoriesMatcher, prevCategoriesMatcher, setSelectedFilters]);

	const slashSelectData = useMemo(() => {
		return [
			{
				title: "Select All",
				isActive: categoryData?.subCategories?.every(i => selectedFilters.categories.includes(i.id ?? 0)),
				onClick: handleSelect(true),
			},
			{
				title: "Clear All",
				isActive: categoryData?.subCategories?.every(i => !selectedFilters.categories.includes(i.id ?? 0)),
				onClick: handleSelect(false),
			},
		]
	}, [categoryData?.subCategories, selectedFilters.categories, handleSelect]);

	const handleSelectSubCategory = useCallback((subCategoryId: number) => () => {
		const filterSubCategory = (categoryId: number) => categoryId !== subCategoryId
		setSelectedFilters((prevFilters) => {
			const isSelected = prevFilters.categories.includes(subCategoryId);
			return {
				...prevFilters,
				categories: isSelected ? prevFilters.categories.filter(filterSubCategory) : [...prevFilters.categories, subCategoryId],
			};
		});
	}, [setSelectedFilters]);

	const subCategories = useMemo(() => {
		if (!isExpanded) {
			return [];
		}
		return categoryData?.subCategories?.map((subCategory, index) => {
			const isSelected = selectedFilters.categories.includes(subCategory.id ?? 0);
			const onClick = handleSelectSubCategory(subCategory.id ?? 0);

			return (
				<div
					className={
						cn(
							"SelectCategoryBlock__item",
							isSelected && "is-selected",
							filteredCategories.includes(subCategory.id ?? 0) && "is-searched",
						)
					}
					key={subCategory.id}
					onClick={onClick}
					data-testid={`buttonCategoryItem_${index}`}
				>
					{subCategory.name}
				</div>
			)
		});
	}, [categoryData?.subCategories, isExpanded, selectedFilters.categories, handleSelectSubCategory, filteredCategories]);

	return (
		<div
			className={
				cn(
					"SelectCategoryBlock",
                    selectedCategoriesCount > 0 && "is-active",
					className,
				)
			}
			data-testid={testId}
		>
			<div className="SelectCategoryBlock__header">
				<div
					className="SelectCategoryBlock__title"
					onClick={handleExpandIconClick}
					data-testid="textCategoryTitle"
				>
					{category.name?.toUpperCase()}
					<FiChevronDown
						className={cn("SelectCategoryBlock__expand-icon", {
							"is-expanded": isExpanded,
						})}
						data-testid="iconCategoryExpandCollapse"
					/>
				</div>
				<div className="SelectCategoryBlock__selection">
					<SlashSelect
						className="SelectCategoryBlock__select SelectCategorySlashSelect"
						data={slashSelectData}
						testId="blockCategorySelectClearAll"
					/>
					<div
						className="SelectCategoryBlock__selection-count"
						data-testid="textCategorySelectCount"
					>
						{selectedCategoriesCount} of {categoryData?.subCategories?.length} selected
					</div>
				</div>
			</div>
			<div
				className="SelectCategoryBlock__items"
				data-testid="blockCategoryItems"
			>
				{subCategories}
			</div>
		</div>
	);
};

export default SelectCategoryBlock;